{ useState } = React
ConnectStore = require 'components/enhancers/connect_store'
AutoCalculateConfirmation = require 'components/nma/autocalculate_confirmation'
{ gdtDataToNmaData } = require 'lib/nma_helper'
IconButton = require 'components/common/icon_button'
mediator = require 'mediator'
{ LeagueTableWithI18n, TableSettingsContext, STANDARD_DENOMINATOR } = NmaComponents
Modal = require 'components/common/modal'
NMAImportModalActions = require 'actions/nma_import_modal_actions'
NMAImportModal = require 'components/nma/nma_import_modal'
NMAVizActions = require 'actions/nma_viz_actions'
NMAOutcomeActions = require 'actions/nma_outcome_actions'
NMAOutcomeStore = require 'stores/nma_outcome_store'
NMAQuestionActions = require 'actions/nma_question_actions'
NMAQuestionStore = require 'stores/nma_question_store'
NMAVizStore = require 'stores/nma_viz_store'
OutcomeThresholds = require 'components/nma/outcome_thresholds'
Popover = require 'components/common/popover'
Spinner = require 'components/common/spinner'
TextAreaWithApply = require 'components/common/text_area_with_apply'
Tooltip = require 'components/common/tooltip'
{ useCoffeeCallback, useCoffeeMemo, useI18n } = require 'lib/react_utils'

storeConnector =
  NMAQuestionStore: (Store) ->
    question: Store.getCurrentQuestion()
  NMAOutcomeStore: (Store) ->
    isFetching: Store.isFetching()
    outcome: Store.getOutcome()
  NMAVizStore: (Store) ->
    autoCalculateModalState: Store.getAutoCalculateModalState()
    vizConfig: Store.getVizConfig().toJS()

NMAOutcome = ({ autoCalculateModalState, isFetching, outcome, question, vizConfig }) ->
  i18n = useI18n 'nma:outcome'
  outcomeId = outcome.get '_id'
  questionId = question.get '_id'
  thresholdType = question.get('thresholdOptions')
  fileName = outcome.get 'fileName'

  [autoCalculatePopoverVisible, setAutoCalculatePopoverVisible] = useState false

  toggleAutoCalculatePopover = ->
    setAutoCalculatePopoverVisible (current) -> not current

  updateDescription = useCoffeeCallback [outcome, outcomeId], (newDescription) ->
    return if outcome.get('description') is newDescription
    NMAOutcomeActions.updateOutcomeDescription outcomeId, newDescription

  onImport = useCoffeeCallback [outcome], ->
    NMAImportModalActions.openImportModal outcome

  onDeleteExplanation = useCoffeeCallback [questionId], (explanationId) ->
    NMAQuestionActions.deleteExplanation questionId, explanationId

  onUpdateExplanations = useCoffeeCallback [questionId], (explanations) ->
    NMAQuestionActions.setExplanations questionId, explanations

  nmaData = useCoffeeMemo [outcome, question], ->
    gdtDataToNmaData question, Immutable.List [outcome]

  tableSettings = useCoffeeMemo [nmaData], ->
    denominator: STANDARD_DENOMINATOR
    outcomes: nmaData.outcomes
    client: vizConfig.client
    showCertaintyIcons: vizConfig.showCertaintyIcons
    showHighlights: vizConfig.showHighlights
    thresholdType: thresholdType

  updateOutcomeData = useCoffeeCallback [outcomeId], (interventionId, comparatorId, newData) ->
    NMAOutcomeActions.updateOutcomeData outcomeId, interventionId, comparatorId, newData

  onOpenAutoCalculationConfirmDialog = useCoffeeCallback [outcomeId], (type) -> ->
    NMAVizActions.openAutoCalculateModal { type, outcomeId }

  return <Spinner /> if isFetching

  <div className="nma-outcome">
    <div className="nma-outcome__description">
      <h4>{i18n 'description'}</h4>
      <TextAreaWithApply
        className="nma-outcome__description-text"
        content={outcome.get('description', '')}
        onApply={updateDescription}
        placeholder={i18n 'description_placeholder'}
      />
    </div>
    <div className="nma-outcome__league-table">
      <div className="league-table__header">
        <div className="flex items-center flex-grow">
          <h4>{i18n 'league_table'}</h4>
          <div className="ml-10">
            <OutcomeThresholds
              outcome={outcome}
              thresholdType={thresholdType}
            />
          </div>
        </div>
        <div className="header__controls">
          <Popover
            visible={autoCalculatePopoverVisible}
            onRequestClose={toggleAutoCalculatePopover}
          >
            <div className="header__auto_calculate_trigger" onClick={toggleAutoCalculatePopover}>
              {i18n '../autocalculation_modal.auto_calculate'}
            </div>
            <div className="autocalculate-options">
              <div className="p-5">
                <Tooltip>
                  <button
                    onClick={onOpenAutoCalculationConfirmDialog("effectClassification")}
                    disabled={not thresholdType or thresholdType is 'None'}
                    title={not thresholdType or thresholdType is 'None' && i18n '../autocalculation_modal.auto_calculate_effect_classification_disabled'}
                  >
                    {i18n '../autocalculation_modal.auto_calculate_effect_classification'}
                  </button>
                </Tooltip>
              </div>
              <div className="p-5">
                <button onClick={onOpenAutoCalculationConfirmDialog("effectInterpretation")}>
                  {i18n '../autocalculation_modal.auto_calculate_effect_interpretation'}
                </button>
              </div>
            </div>
          </Popover>

          {if fileName
            <Tooltip>
              <div className="header__file-name" title={fileName}>
                <div>{fileName}</div>
              </div>
            </Tooltip>
          else
            <Tooltip>
              <IconButton
                iconName="import"
                onClick={onImport}
                title={i18n '/actions.import'}
              />
            </Tooltip>
          }
        </div>
      </div>
      <div>
        <TableSettingsContext.Provider value={tableSettings}>
          <LeagueTableWithI18n
            comparisons={nmaData.comparisons}
            deleteExplanation={onDeleteExplanation}
            explanations={nmaData.explanations}
            i18nextInstance={i18next}
            interventions={nmaData.interventions}
            outcome={nmaData.outcomes[0] ? {}}
            showCoeInsufficientLabel={mediator.services.switches.isServerSwitchOn('acpFeatures')}
            thresholdType={question.get('thresholdOptions')}
            updateExplanations={onUpdateExplanations}
            updateOutcomeData={updateOutcomeData}
          />
        </TableSettingsContext.Provider>
      </div>
    </div>
    {autoCalculateModalState.get('isOpen') and <Modal
      className="nma-viz-autocalculate"
      closeButton
      isOpen
      title={i18next.t "nma:autocalculation_modal.title_#{_.str.underscored(autoCalculateModalState.get('type'))}"}
      onClose={NMAVizActions.closeAutoCalculateModal}
    >
      <AutoCalculateConfirmation
        autoCalculateModalState={autoCalculateModalState}
        onApply={NMAVizActions.autoCalculateEffectClassification}
        onCancel={NMAVizActions.closeAutoCalculateModal}
      />
    </Modal>}
    <NMAImportModal />
  </div>

NMAOutcome.propTypes =
  isFetching: PropTypes.bool.isRequired
  outcome: PropTypes.instanceOf(Immutable.Map).isRequired
  question: PropTypes.instanceOf(Immutable.Map).isRequired

module.exports = ConnectStore NMAOutcome, [NMAOutcomeStore, NMAQuestionStore, NMAVizStore], storeConnector
